import React, {useCallback, useEffect, useState} from 'react';
import {useAuthenticationPrompt} from '../AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';

interface AuthenticatedViewProps<ViewProps extends object> {
  context: `${string}-page`;
  /**
   * If true, the modal will display the guest option
   */
  allowGuestLogin?: boolean;

  /**
   * View component to return if the authentication is successful
   */
  View: React.ComponentType<ViewProps>;

  /**
   * Any additional props to provide to the View component
   */
  viewProps?: React.ComponentProps<React.ComponentType<ViewProps>>;

  /**
   * Component to show if the user is not authenticated
   */
  Loading?: React.ComponentType;

  /**
   * Component to show if the user cannot be authenticated e.g. they close the modal
   */
  Fallback: React.ComponentType;
}

enum RENDERED {
  VIEW = 'VIEW',
  FALLBACK = 'FALLBACK',
  LOADING = 'LOADING',
}

export const AuthenticatedView = <ViewProps extends object>({
  View: ViewComponent,
  Loading: LoadingComponent,
  Fallback: FallbackComponent,
  allowGuestLogin,
  viewProps,
  context,
}: AuthenticatedViewProps<ViewProps>) => {
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const {isUserDataLoading} = useIsAuthenticated();

  const [renderedComponent, setRenderedComponent] = useState<RENDERED>(
    RENDERED.LOADING,
  );

  const StaleViewComponent = useCallback(
    ViewComponent as any,
    [],
  ) as typeof ViewComponent;

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (isUserDataLoading) {
      return;
    }

    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setRenderedComponent(RENDERED.LOADING);

    showUserAuthenticationPrompt({
      context: `${context}-unauthenticated`,
      /**
       * Pages wrapped in the AuthenticatedView always requires checkout session to be valid
       */
      needsCheckoutSession: true,
      allowGuestLogin,
      onCancel: () => {
        // `setTimeout` fixes a race condition where if the fallback component also does a history change (e.g. `<Redirect />`),
        // it would not be applied correctly, since after the history change the cancel method does a `history.goBack()`,
        // which would override the previous change.
        setTimeout(() => {
          setRenderedComponent(RENDERED.FALLBACK);
        });
      },
      onSuccess: () => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setRenderedComponent(RENDERED.VIEW);
      },
    });
  }, [allowGuestLogin, isUserDataLoading]);

  switch (renderedComponent) {
    case RENDERED.VIEW:
      return <StaleViewComponent {...(viewProps as ViewProps)} />;
    case RENDERED.FALLBACK:
      return <FallbackComponent key={`${Math.random()}`} />;
    case RENDERED.LOADING:
      return LoadingComponent ? <LoadingComponent /> : null;
  }
};
